import React, { createContext, useContext, useEffect, useState } from 'react';

interface ScrollContextType {
  scrollY: number;
  scrollProgress: number;
  isScrolling: boolean;
}

const ScrollContext = createContext<ScrollContextType>({
  scrollY: 0,
  scrollProgress: 0,
  isScrolling: false,
});

export const useScroll = () => useContext(ScrollContext);

export const ScrollProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const updateScroll = () => {
      const currentScrollY = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(currentScrollY / maxScroll, 1);

      setScrollY(currentScrollY);
      setScrollProgress(progress);
      setIsScrolling(true);

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', updateScroll, { passive: true });
    updateScroll();

    return () => {
      window.removeEventListener('scroll', updateScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return (
    <ScrollContext.Provider value={{ scrollY, scrollProgress, isScrolling }}>
      {children}
    </ScrollContext.Provider>
  );
};
